import { useInRecordSection, useInSectionEditForm } from 'components/section-edit/SectionEdit';
import { useSectionActions } from 'components/section-menu/SectionActionsContext';
import { Checkbox } from 'components/ui/checkbox';
import { FieldError } from 'components/ui/field';
import { Switch } from 'components/ui/switch';
import { useId } from 'react';
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import ReadonlyField from '../rhf-other/ReadonlyField';
import ChangeReasonInput from './ChangeReasonInput';
import { CommonInputProps } from './InputTypes';
import InputWrapper from './InputWrapper';
import { useMemoizedRules } from './useMemoizedRules';

interface Props<T extends FieldValues> extends CommonInputProps<T> {
    onChange?: (value: boolean) => void;
    readonly?: boolean;
    /** The box shows the opposite of the stored value: a positive label over a field that stores the negation. */
    inverted?: boolean;
    /** A box with its label beside it, even where a section draws its yes/no fields as switches. */
    keepCheckbox?: boolean;
}

const CheckboxInput = <T extends FieldValues>({
    name,
    control,
    disabled,
    label,
    required,
    rightLabelComponent,
    shouldTranslateLabel = true,
    wrapperClassName,
    validate,
    onChange,
    readonly = false,
    tooltip,
    withChangeReason = false,
    inverted = false,
    keepCheckbox = false,
}: Props<T>) => {
    const rules = useMemoizedRules({ required, validate });
    const reasonFieldName = `changeReasons.${name}` as Path<T>;
    const { hideEmptyFields } = useSectionActions();
    // A record section in edit, and a section of the create dialog, draws a yes/no field as a labelled switch
    // cell, as its other fields are drawn. A record section has the value's text first, where its read view has
    // it; a dialog has it after the switch.
    const asSwitch = useInSectionEditForm() && !keepCheckbox;
    const inRecordSection = useInRecordSection();
    const errorId = useId();

    return (
        <>
            <Controller
                name={name}
                control={control}
                rules={rules}
                render={({ field: { ref, value: stored, onBlur, onChange: innerOnChange }, fieldState: { error } }) => {
                    // What the box shows, and the stored value a tick gives.
                    const value = inverted ? !stored : stored;
                    const change = (checked: boolean) => {
                        const next = inverted ? !checked : checked;
                        innerOnChange(next as PathValue<T, Path<T>>);
                        onChange?.(next);
                    };
                    return readonly ? (
                        <InputWrapper
                            name={name}
                            label={label}
                            required={required}
                            readonly={readonly}
                            rightLabelComponent={rightLabelComponent}
                            shouldTranslateLabel={shouldTranslateLabel}
                            hasErrors={!!error}
                            tooltip={tooltip}
                        >
                            <ReadonlyField value={value ? t('yes') : t('no')} />
                        </InputWrapper>
                    ) : asSwitch ? (
                        <InputWrapper
                            className={cn(hideEmptyFields && !required && !value && 'hidden')}
                            name={name}
                            label={label}
                            required={required}
                            rightLabelComponent={rightLabelComponent}
                            shouldTranslateLabel={shouldTranslateLabel}
                            hasErrors={!!error}
                            error={error}
                            tooltip={tooltip}
                        >
                            <div className="flex h-9 items-center gap-2.5">
                                {inRecordSection ? (
                                    <span className="text-foreground min-w-7 text-sm font-medium">
                                        {value ? t('yes') : t('no')}
                                    </span>
                                ) : null}
                                <Switch
                                    id={name}
                                    name={name}
                                    ref={ref}
                                    disabled={disabled}
                                    onBlur={onBlur}
                                    aria-invalid={!!error}
                                    checked={value === undefined || value === null ? false : value}
                                    onCheckedChange={change}
                                />
                                {inRecordSection ? null : (
                                    <span className="text-foreground text-sm">{value ? t('yes') : t('no')}</span>
                                )}
                            </div>
                        </InputWrapper>
                    ) : (
                        <div className={cn('flex flex-col', hideEmptyFields && !required && !value && 'hidden')}>
                            {/* min-h-8: the row height that spaces stacked checkboxes. */}
                            <div className={cn('flex min-h-8 flex-row items-center gap-1.5', wrapperClassName)}>
                                <div
                                    className={cn('flex flex-row items-center gap-2', disabled && 'cursor-not-allowed')}
                                >
                                    <Checkbox
                                        id={name}
                                        name={name}
                                        ref={ref}
                                        variant="secondary"
                                        disabled={disabled}
                                        onBlur={onBlur}
                                        aria-invalid={!!error}
                                        aria-describedby={error ? errorId : undefined}
                                        checked={value === undefined || value === null ? false : value}
                                        onCheckedChange={change}
                                    />
                                    {label && (
                                        <label
                                            htmlFor={name}
                                            className={cn(
                                                'text-fg-secondary text-sm',
                                                error && 'text-destructive new:text-danger-ink',
                                                disabled && 'cursor-not-allowed opacity-40'
                                            )}
                                        >
                                            {shouldTranslateLabel ? t(label) : label}
                                            {required ? (
                                                <span className="text-destructive new:text-danger-ink">&thinsp;*</span>
                                            ) : null}
                                        </label>
                                    )}
                                </div>
                                {tooltip ? tooltip : null}
                                {rightLabelComponent}
                            </div>
                            {/* The checkbox's description, not an alert of its own. */}
                            <FieldError
                                id={errorId}
                                role={undefined}
                                className="mt-1"
                                errors={error ? [error] : undefined}
                            />
                        </div>
                    );
                }}
            />
            {withChangeReason ? <ChangeReasonInput control={control} name={reasonFieldName} required={true} /> : null}
        </>
    );
};

export default CheckboxInput;
